import { useCallback } from "react";
import { z } from "zod";

import { fetchFastApi } from "../../api/fastapi";
import { analyticsPath, type AnalyticsOwner } from "../../lib/analyticsOwner";
import {
  AnalyticsWorkflowError,
  toAnalyticsWorkflowError,
} from "../../lib/analyticsWorkflow";

/**
 * Where to open the workflow version one verdict's run executed, minted per
 * click.
 *
 * Fetched when the auditor presses the link rather than carried on the status
 * payload, for the reason the column's own workflow link is: the URL holds a
 * live Cascade session credential, and the status read is polled — an `href`
 * composed from it would hold that credential in the DOM for as long as the
 * pane is open, and re-mint it on every poll.
 *
 * Addressed by the verdict and not by the column, because the two name
 * different destinations: the column's link opens whichever version Optro
 * Analytics has activated now, and this one opens the version that produced the
 * result on screen. Reading the column's link into the review pane would say it
 * opens the version that ran and open a later one.
 *
 * `null` is an answer and not a failure: it means there is no page for that
 * version, which the caller states rather than opening a blank tab onto
 * nothing.
 *
 * **The route's suffix and its one field are the whole of this contract, and
 * they live in this file alone** — deliberately not folded into
 * `useAnalyticsWorkflows`, whose shapes are settled, so that the endpoint being
 * built against this pane can be re-pointed or re-spelled in one place. The
 * owner's base path comes from `analyticsPath`, like every other analytics read.
 */
const executedVersionLinkPath = (owner: AnalyticsOwner) =>
  `${analyticsPath(owner)}/executed-version-link`;

/**
 * The one field, required rather than defaulted to null.
 *
 * Null is a real answer here — no page for that version — so a default would
 * let a body that never carried the field at all answer it, and every click
 * would report a version that cannot be opened while the route was in fact
 * answering something this build does not read. An absent field is drift, and
 * drift is worth failing on.
 */
const workflowVersionLinkSchema = z.object({
  url: z.string().nullable(),
});

/**
 * A body that got past the request but not the schema, reported as the one
 * failure a retry cannot clear: the server answers the same drifted shape until
 * the page picks up a client that matches it.
 */
export const UNREADABLE_LINK_RESPONSE =
  "The address of the version this test ran could not be read. Reload the page and try again.";

export function useAnalyticsWorkflowVersionLink(
  owner: AnalyticsOwner | undefined,
): (columnId: string, sourceRowId: string) => Promise<string | null> {
  const ownerKind = owner?.kind;
  const ownerId = owner?.id;
  return useCallback(
    async (columnId: string, sourceRowId: string) => {
      if (!ownerKind || !ownerId) {
        // Typed, because no request is made: an untyped throw would reach the
        // link's connectivity default and blame a network that was never used.
        throw new AnalyticsWorkflowError(
          "unknown",
          0,
          "An analytics workflow link read needs a control or work step id",
        );
      }

      // The cell, not the verdict: the route keys on the run its manifest
      // names, and addresses that run by the column and row that hold it.
      const search = new URLSearchParams({ columnId, sourceRowId }).toString();

      let response: unknown;
      try {
        response = await fetchFastApi({
          method: "GET",
          // The search is appended to the path rather than passed as `query`,
          // as `requestAnalytics` appends its own: when `fetchFastApi`
          // re-issues a request after refreshing an expired access token it
          // forwards only the body, method, path, retry count and
          // error-envelope flag, so a `query` handed to it separately is
          // dropped from the retry. Both parameters are required by the route,
          // which would answer 422 — the first click after a token expires
          // would fail and the next one succeed, making a deterministic failure
          // read as an intermittent one.
          path: `${executedVersionLinkPath({ kind: ownerKind, id: ownerId })}?${search}`,
          throwOnErrorEnvelope: true,
        });
      } catch (error) {
        throw toAnalyticsWorkflowError(error);
      }

      // Parsed outside the catch above, so a drifted body is its own failure
      // rather than the request's and never travels as a `ZodError` message.
      const parsed = workflowVersionLinkSchema.safeParse(response);
      if (!parsed.success) {
        // The browser console is the only place the issues survive, matching
        // how the other analytics reads report an unreadable payload.
        console.error(
          "Unreadable analytics workflow version link:",
          parsed.error.issues,
        );
        throw new AnalyticsWorkflowError(
          "unreadable",
          0,
          UNREADABLE_LINK_RESPONSE,
        );
      }
      return parsed.data.url;
    },
    [ownerKind, ownerId],
  );
}
